/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Button, Card } from "core/components";
import { useFormContext, useFieldArray } from "react-hook-form";
import { TaskFormData, TemplateTaskItemFormData } from "core/schema";
import TemplateTaskItemForm from "./TemplateTaskItemForm";
import TemplateTaskItemsContext from "./TemplateTaskItemsContext";

type TemplateTaskItemsFormProps = {
  tasks: Array<TaskFormData>;
  is_purchasing: boolean;
};

const TemplateTaskItemsForm = ({ parentIndex, task, is_purchasing, items }: TemplateTaskItemsFormProps): JSX.Element => {
  const fieldArrayName = `template_tasks_attributes[${parentIndex}].template_task_items_attributes`;

  const {
    control,
    formState: { errors }
  } = useFormContext();
  const { fields, append, remove } = useFieldArray<TemplateTaskItemFormData>({
    control,
    name: fieldArrayName,
    keyName: "key"
  });
  const [selectedItems, setSelectedItems] = React.useState<Array<TaskFormData>>([]);
  const [itemsForTask, setItemsForTask] = React.useState<Array<Record<string, unknown>>>(items);

  React.useEffect(() => {
    setItemsForTask(items.filter((item) => task?.item_subcategories.includes(item.item_subcategory_id)));
  }, [fields, task]);

  const canAppendItem = () => {
    return itemsForTask.length > fields.length && !is_purchasing;
  };

  return (
    <TemplateTaskItemsContext.Provider value={{ selectedItems, setSelectedItems }}>
      <Card>
        <Card.Header title="Task Items" titleProps={{ className: "h5" }}>
          {errors.template_tasks_attributes?.[parentIndex]?.template_task_items_attributes?.length > 0 && (
            <Button className="button--danger mr-2" icon="exclamation-triangle" />
          )}
        </Card.Header>
        <Card.Body>
          {fields.map((item, index) => {
            return (
              <TemplateTaskItemForm
                key={item.key}
                parentIndex={parentIndex}
                index={index}
                remove={remove}
                append={append}
                is_purchasing={is_purchasing}
                items={itemsForTask}
              />
            );
          })}
          {canAppendItem() && (
            <Button
              className="button--brand mt-4"
              title="Add Task Item"
              icon="plus"
              type="button"
              onClick={() => {
                append({ budgeted_quantity: 0.0 });
              }}
            />
          )}
        </Card.Body>
      </Card>
    </TemplateTaskItemsContext.Provider>
  );
};

export default TemplateTaskItemsForm;
